import { clearSession, loadSession, saveSession } from "./auth";

/**
 * Best-effort server-side logout: invalidates the refresh token on Supabase
 * so it can't be reused after the user logs out. Failure is fine — we always
 * clear the local session regardless.
 */
export async function logout(): Promise<void> {
  const s = loadSession();
  if (s) {
    try {
      await fetch("/v1/auth/logout", {
        method: "POST",
        headers: { Authorization: `Bearer ${s.accessToken}` },
      });
    } catch {
      // Network error — fall through; localStorage clear is enough.
    }
  }
  clearSession();
}

const BASE = "/v1";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function refresh(): Promise<string | null> {
  const s = loadSession();
  if (!s) return null;
  const res = await fetch(`${BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: s.refreshToken }),
  });
  if (!res.ok) {
    clearSession();
    return null;
  }
  const data = (await res.json()) as {
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
  };
  saveSession({ ...s, ...data });
  return data.accessToken;
}

export async function api<T = unknown>(
  path: string,
  opts: {
    method?: string;
    body?: unknown;
    formData?: FormData;
    auth?: boolean;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;

  if (opts.formData) {
    body = opts.formData;
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  if (opts.auth !== false) {
    const s = loadSession();
    if (s) headers.Authorization = `Bearer ${s.accessToken}`;
  }

  let res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? (body ? "POST" : "GET"),
    headers,
    body,
  });

  // One-shot refresh attempt on 401.
  if (res.status === 401 && opts.auth !== false) {
    const newToken = await refresh();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      res = await fetch(`${BASE}${path}`, {
        method: opts.method ?? (body ? "POST" : "GET"),
        headers,
        body,
      });
    }
  }

  if (!res.ok) {
    let code = "error";
    let message = res.statusText;
    try {
      const j = (await res.json()) as { error?: string; message?: string };
      code = j.error ?? code;
      message = j.message ?? code;
    } catch {
      /* ignore non-JSON */
    }
    throw new ApiError(res.status, code, message);
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return (await res.json()) as T;
  return (await res.blob()) as unknown as T;
}
